/* Common styles shared across pages */

/* Card hover effects */
.hoverCard {
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  cursor: pointer;
}

.hoverCard:hover {
  transform: translateY(-2px);
  box-shadow: var(--mantine-shadow-md);
}

/* Table row hover */
.tableRow {
  transition: background-color 0.15s ease;
}

.tableRow:hover {
  background-color: light-dark(
    var(--mantine-color-gray-0),
    var(--mantine-color-dark-6)
  );
}

/* Loading skeleton */
.skeleton {
  animation: shimmer 1.5s infinite;
  background: linear-gradient(
    90deg,
    light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-6)) 25%,
    light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-5)) 50%,
    light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-6)) 75%
  );
  background-size: 200% 100%;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Action icons */
.actionIcon {
  transition: transform 0.2s ease;
}

.actionIcon:hover {
  transform: scale(1.1);
}

/* Badge animations */
.badge {
  transition: transform 0.15s ease;
}

.badge:hover {
  transform: scale(1.05);
}

/* Button hover effects */
.actionButton {
  transition: all 0.2s ease;
}

.actionButton:hover {
  transform: translateX(2px);
}

/* Critical/warning states */
.critical {
  color: var(--mantine-color-red-6);
  font-weight: 600;
}

.warning {
  color: var(--mantine-color-yellow-6);
  font-weight: 600;
}

.success {
  color: var(--mantine-color-green-6);
  font-weight: 600;
}

.info {
  color: var(--mantine-color-blue-6);
}

/* Empty state */
.emptyState {
  padding: var(--mantine-spacing-xl);
  text-align: center;
  color: var(--mantine-color-dimmed);
}

/* Section header */
.sectionHeader {
  margin-bottom: var(--mantine-spacing-md);
  padding-bottom: var(--mantine-spacing-sm);
  border-bottom: 1px solid
    light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-2));
}

/* Status badges with semantic colors */
.statusActive {
  background-color: var(--mantine-color-green-1);
  color: var(--mantine-color-green-9);
}

.statusInactive {
  background-color: var(--mantine-color-gray-1);
  color: var(--mantine-color-gray-9);
}

.statusPending {
  background-color: var(--mantine-color-yellow-1);
  color: var(--mantine-color-yellow-9);
}

.statusCritical {
  background-color: var(--mantine-color-red-1);
  color: var(--mantine-color-red-9);
}

/*
 * Touch target — 44px tap area without a 44px button.
 *
 * The app's header icons are 34px and its sidebar rows 41px, both under the
 * 44px touch minimum (WCAG 2.5.5 / Apple HIG) that the iPad point-of-care
 * shell is held to. Growing the buttons themselves would cost header width
 * and sidebar height on a UI we have been deliberately tightening, so the
 * BUTTON stays its drawn size and only the HIT REGION grows: an invisible
 * centered overlay, sized to whichever is larger — the element or 44px.
 *
 * `max()` matters. A flat 44px would SHRINK the hit area of anything already
 * larger, which is the opposite of the point.
 *
 * Callers must keep >=10px between adjacent targets, or the invisible regions
 * overlap and taps land on the wrong control — worse than a small button.
 * That is why the header icon group runs at gap="sm", not "xs".
 */
.touchTarget {
  position: relative;
  /*
   * Required, not cosmetic. Mantine's ActionIcon root sets `overflow: hidden`,
   * which CLIPS the overlay below back to the button's own box — the element
   * still computes to 44px, so getComputedStyle reports success while
   * document.elementFromPoint 4px outside returns the parent. The hit region
   * is only real with this line. Verify changes here by hit-testing a point
   * outside the drawn button, never by reading the computed width.
   */
  overflow: visible;
}

.touchTarget::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  translate: -50% -50%;
  width: max(100%, 44px);
  height: max(100%, 44px);
  /* Invisible, and never paints over the icon it belongs to. */
  background: transparent;
}
